// src/components/payment/PlanCard.tsx
// Subscription plan card — shows plan name, price, savings badge, feature list,
// and selection radio dot. Stateless: selection is fully controlled by parent.

import React, { useState } from 'react';
import type { Plan } from '../../services/paymentService';

// ── Props ─────────────────────────────────────────────────────────────────────

interface PlanCardProps {
  plan: Plan;
  isSelected: boolean;
  onSelect: () => void;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function PlanCard({ plan, isSelected, onSelect }: PlanCardProps): React.JSX.Element {
  const [isHovered, setIsHovered] = useState(false);

  const accentColor = plan.color;

  const styles: Record<string, React.CSSProperties> = {
    card: {
      position: 'relative',
      background: isSelected
        ? `rgba(${hexToRgb(accentColor)}, 0.10)`
        : isHovered
        ? 'rgba(255,255,255,0.055)'
        : 'rgba(255,255,255,0.03)',
      border: `1.5px solid ${
        isSelected ? accentColor : isHovered ? 'rgba(255,255,255,0.14)' : 'rgba(255,255,255,0.07)'
      }`,
      borderRadius: '18px',
      padding: plan.badge ? '32px 20px 22px' : '22px 20px 22px',
      display: 'flex',
      flexDirection: 'column',
      cursor: 'pointer',
      transition: 'background 0.25s ease, border-color 0.25s ease, transform 0.25s ease, box-shadow 0.25s ease',
      transform: isHovered && !isSelected ? 'translateY(-4px)' : 'translateY(0)',
      boxShadow: isSelected
        ? `0 0 0 4px ${hexToRgba(accentColor, 0.12)}, 0 12px 32px rgba(0,0,0,0.35)`
        : isHovered
        ? '0 8px 24px rgba(0,0,0,0.25)'
        : 'none',
      outline: 'none',
      userSelect: 'none',
    },

    badge: {
      position: 'absolute',
      top: '-13px',
      left: '50%',
      transform: 'translateX(-50%)',
      whiteSpace: 'nowrap',
      fontSize: '0.7rem',
      fontWeight: 700,
      letterSpacing: '0.04em',
      color: '#fff',
      background: `linear-gradient(135deg, ${accentColor}, ${lightenHex(accentColor)})`,
      padding: '4px 14px',
      borderRadius: '20px',
      boxShadow: `0 4px 14px ${hexToRgba(accentColor, 0.4)}`,
    },

    name: {
      fontSize: '0.72rem',
      fontWeight: 700,
      letterSpacing: '0.1em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.45)',
      marginBottom: '10px',
    },

    priceRow: {
      display: 'flex',
      alignItems: 'flex-start',
      lineHeight: 1,
      marginBottom: '4px',
    },

    priceCurrency: {
      fontSize: '1rem',
      fontWeight: 700,
      color: accentColor,
      marginTop: '5px',
      marginRight: '1px',
    },

    priceWhole: {
      fontFamily: "'Syne', sans-serif",
      fontSize: '2.2rem',
      fontWeight: 800,
      color: accentColor,
      lineHeight: 1,
    },

    priceCents: {
      fontSize: '1.1rem',
      fontWeight: 700,
      color: accentColor,
      marginTop: '4px',
    },

    period: {
      fontSize: '0.8rem',
      color: 'rgba(255,255,255,0.3)',
      marginBottom: '10px',
    },

    savingsBadge: {
      display: 'inline-block',
      fontSize: '0.7rem',
      fontWeight: 700,
      color: '#00b894',
      background: 'rgba(0,184,148,0.12)',
      padding: '3px 10px',
      borderRadius: '12px',
      marginBottom: '16px',
    },

    featureList: {
      listStyle: 'none',
      padding: 0,
      margin: '0 0 18px',
      flex: 1,
    },

    featureItem: {
      display: 'flex',
      alignItems: 'flex-start',
      gap: '8px',
      fontSize: '0.82rem',
      color: 'rgba(255,255,255,0.55)',
      padding: '3px 0',
      lineHeight: 1.4,
    },

    featureCheck: {
      color: accentColor,
      fontWeight: 700,
      fontSize: '0.88rem',
      flexShrink: 0,
      marginTop: '1px',
    },

    radio: {
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      border: isSelected ? `2px solid ${accentColor}` : '2px solid rgba(255,255,255,0.2)',
      margin: '0 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: isSelected ? accentColor : 'transparent',
      boxShadow: isSelected ? `0 0 12px ${hexToRgba(accentColor, 0.5)}` : 'none',
      transition: 'all 0.2s ease',
      flexShrink: 0,
    },

    radioDot: {
      width: '6px',
      height: '6px',
      borderRadius: '50%',
      background: '#fff',
      opacity: isSelected ? 1 : 0,
      transition: 'opacity 0.2s ease',
    },
  };

  // Split price into whole and cents parts
  const priceStr = plan.price.toFixed(2);
  const [whole, cents] = priceStr.split('.');

  return (
    <div
      style={styles.card}
      role="radio"
      aria-checked={isSelected}
      tabIndex={0}
      onClick={onSelect}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onSelect();
        }
      }}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {/* Top badge (e.g. "🔥 Best Value") */}
      {plan.badge && <div style={styles.badge}>{plan.badge}</div>}

      {/* Plan name */}
      <div style={styles.name}>{plan.name}</div>

      {/* Price */}
      <div style={styles.priceRow}>
        <span style={styles.priceCurrency}>$</span>
        <span style={styles.priceWhole}>{whole}</span>
        <span style={styles.priceCents}>.{cents}</span>
      </div>

      {/* Period */}
      <div style={styles.period}>{plan.period}</div>

      {/* Savings badge */}
      {plan.savings && <span style={styles.savingsBadge}>{plan.savings}</span>}

      {/* Features */}
      <ul style={styles.featureList}>
        {plan.features.map((feature) => (
          <li key={feature} style={styles.featureItem}>
            <span style={styles.featureCheck}>✓</span>
            <span>{feature}</span>
          </li>
        ))}
      </ul>

      {/* Selection radio */}
      <div style={styles.radio}>
        <div style={styles.radioDot} />
      </div>
    </div>
  );
}

// ── Color utilities ───────────────────────────────────────────────────────────
// Pure functions — no external deps.

function hexToRgb(hex: string): string {
  const clean = hex.replace('#', '');
  const r = parseInt(clean.slice(0, 2), 16);
  const g = parseInt(clean.slice(2, 4), 16);
  const b = parseInt(clean.slice(4, 6), 16);
  return `${r}, ${g}, ${b}`;
}

function hexToRgba(hex: string, alpha: number): string {
  return `rgba(${hexToRgb(hex)}, ${alpha})`;
}

/** Returns a slightly lighter version of a hex color for gradient ends. */
function lightenHex(hex: string): string {
  const clean  = hex.replace('#', '');
  const factor = 1.25;
  const r = Math.min(255, Math.round(parseInt(clean.slice(0, 2), 16) * factor));
  const g = Math.min(255, Math.round(parseInt(clean.slice(2, 4), 16) * factor));
  const b = Math.min(255, Math.round(parseInt(clean.slice(4, 6), 16) * factor));
  return `rgb(${r}, ${g}, ${b})`;
}